import { useEffect, useRef, useState } from "react";
import type { TiltScoutSnapshot } from "./tilt-scout-types";
import {
  ARROW_SIZE_PX,
  ARROW_SLOT_WIDTH_PX,
  DOT_HEIGHT_PX,
  DOT_THRESHOLD,
  DOT_WIDTH_PX,
  FUSE_HEIGHT_PX,
  FUSE_WIDTH_PX,
  activeRadius,
  dotFill,
  dotHairline,
  fuseContainerBackground,
  fuseContainerShadow,
  lavaInsetShadow,
  morphValue,
  postureColor,
  segmentClip,
} from "./tilt-scout-fuse-utils";

interface TiltScoutFuseVisualProps {
  score: number;
  posture: TiltScoutSnapshot["posture"];
}

export function TiltScoutFuseVisual(props: TiltScoutFuseVisualProps) {
  return (
    <>
      <HeaderArrow direction="down" color="var(--fintheon-bearish)" />
      <HeaderFuse {...props} />
      <HeaderArrow direction="up" color="var(--fintheon-bullish)" />
    </>
  );
}

function HeaderFuse({ score, posture }: TiltScoutFuseVisualProps) {
  const shownScore = score;
  const shownPosture = posture;
  const pulseScore = Math.round(shownScore * 10) / 10;
  const previousPulseScoreRef = useRef(pulseScore);
  const [scorePulseToken, setScorePulseToken] = useState(0);
  useEffect(() => {
    if (previousPulseScoreRef.current !== pulseScore) {
      setScorePulseToken((value) => value + 1);
    }
    previousPulseScoreRef.current = pulseScore;
  }, [pulseScore]);
  const strength =
    shownPosture === "neutral" ? 0 : Math.min(1, Math.abs(shownScore) / 10);
  const magnitude = Math.abs(shownScore);
  const side = shownScore < 0 ? "left" : "right";
  const fillWidth = Math.max(
    DOT_WIDTH_PX,
    strength * (FUSE_WIDTH_PX / 2) + DOT_WIDTH_PX / 2,
  );
  const morphStrength = morphValue(magnitude);
  const tipPx = Math.round(5 + morphStrength * 8);
  const color = postureColor(shownPosture);
  const active = magnitude >= DOT_THRESHOLD;
  const hasLean = shownPosture !== "neutral";
  const dotColor = hasLean ? color : "var(--fintheon-tilt)";
  const shimmerAnimation = scorePulseToken
    ? "nothing-fuse-shimmer 920ms linear 1"
    : "none";
  const fillPosition =
    side === "left"
      ? { right: `calc(50% - ${DOT_WIDTH_PX / 2}px)` }
      : { left: `calc(50% - ${DOT_WIDTH_PX / 2}px)` };

  return (
    <span
      data-tiltscout-part="fuse-container"
      className="relative mx-auto block overflow-hidden"
      style={{
        background: fuseContainerBackground(),
        borderRadius: 999,
        boxShadow: fuseContainerShadow(),
        height: FUSE_HEIGHT_PX,
        width: FUSE_WIDTH_PX,
      }}
      aria-hidden
    >
      {active && (
        <span
          data-tiltscout-part="fuse-read"
          className="absolute bottom-0 top-0 overflow-hidden"
          style={{
            ...fillPosition,
            background: color,
            borderRadius: activeRadius(side, morphStrength, strength),
            boxShadow: lavaInsetShadow(color),
            clipPath: segmentClip(side, tipPx, strength, morphStrength),
            transition:
              "clip-path 220ms ease, width 220ms ease, border-radius 220ms ease",
            width: fillWidth,
          }}
        >
          <span
            key={`read-shimmer-${scorePulseToken}`}
            className="nothing-fuse-shimmer"
            style={{
              animation: shimmerAnimation,
              opacity: scorePulseToken ? undefined : 0,
            }}
          />
        </span>
      )}
      <span
        key={`neutral-dot-${scorePulseToken}`}
        data-tiltscout-part="neutral-dot"
        className="absolute left-1/2 top-1/2 overflow-hidden"
        style={{
          animation: "none",
          background: dotFill(dotColor),
          borderRadius: 999,
          boxShadow: dotHairline(dotColor),
          height: DOT_HEIGHT_PX,
          opacity: active ? 0 : 1,
          transform: "translate(-50%, -50%)",
          width: DOT_WIDTH_PX,
        }}
      >
        <span
          className="nothing-fuse-shimmer"
          style={{
            animation: shimmerAnimation,
            opacity: scorePulseToken ? undefined : 0,
          }}
        />
      </span>
    </span>
  );
}

function HeaderArrow({
  direction,
  color,
}: {
  direction: "down" | "up";
  color: string;
}) {
  return (
    <span
      data-tiltscout-part={`arrow-${direction}`}
      className="relative block"
      style={{
        color,
        height: FUSE_HEIGHT_PX,
        width: ARROW_SLOT_WIDTH_PX,
      }}
      aria-hidden
    >
      <svg
        data-tiltscout-part={`arrow-${direction}-glyph`}
        viewBox={`0 0 ${ARROW_SLOT_WIDTH_PX} ${FUSE_HEIGHT_PX}`}
        preserveAspectRatio="none"
        shapeRendering="geometricPrecision"
        style={{
          position: "absolute",
          inset: 0,
        }}
      >
        <path d={roundedArrowPath(direction)} fill="currentColor" />
      </svg>
    </span>
  );
}

function roundedArrowPath(direction: "down" | "up") {
  const width = ARROW_SIZE_PX;
  const slot = ARROW_SLOT_WIDTH_PX;
  const height = FUSE_HEIGHT_PX;
  const left = (slot - width) / 2;
  const right = left + width;
  const center = slot / 2;
  const corner = 1.65;
  const tip = 1.35;

  if (direction === "down") {
    return [
      `M ${left + corner} 0`,
      `L ${right - corner} 0`,
      `Q ${right} 0 ${right - corner * 0.45} ${corner}`,
      `L ${center + tip} ${height - tip}`,
      `Q ${center} ${height} ${center - tip} ${height - tip}`,
      `L ${left + corner * 0.45} ${corner}`,
      `Q ${left} 0 ${left + corner} 0`,
      "Z",
    ].join(" ");
  }

  return [
    `M ${center - tip} ${tip}`,
    `Q ${center} 0 ${center + tip} ${tip}`,
    `L ${right - corner * 0.45} ${height - corner}`,
    `Q ${right} ${height} ${right - corner} ${height}`,
    `L ${left + corner} ${height}`,
    `Q ${left} ${height} ${left + corner * 0.45} ${height - corner}`,
    "Z",
  ].join(" ");
}
